import {CubeFieldSpec} from '@xh/hoist/data';
import {endsWith} from 'lodash';
import {DistributionsModel} from './DistributionsModel';
import {ColumnSpec} from '@xh/hoist/cmp/grid';
import {fmtDate, fmtDateTimeSec, fmtPrice} from '@xh/hoist/format';
import {actionCol, calcActionColWidth, dateEditor, numberEditor, selectEditor} from '@xh/hoist/desktop/cmp/grid';
import {div} from '@xh/hoist/cmp/layout';
import {Icon} from '@xh/hoist/icon';
import {badge} from '@xh/hoist/cmp/badge';
import classNames from 'classnames';

const ownerUserIdField: CubeFieldSpec = {
    name: 'ownerUserId',
    type: 'int'
};

const portfolioIdField: CubeFieldSpec = {
    name: 'portfolioId',
    type: 'int'
};

const portfolioNameField: CubeFieldSpec = {
    name: 'portfolioName',
    type: 'string'
};

const securityIdField: CubeFieldSpec = {
    name: 'securityId',
    type: 'int'
};

const securityNameField: CubeFieldSpec = {
    name: 'securityName',
    displayName: 'Deal',
    type: 'string',
    isDimension: true
};

const entityField: CubeFieldSpec = {
    name: 'entity',
    type: 'string',
    isDimension: true
};

const actionField: CubeFieldSpec = {
    name: 'action',
    type: 'string',
    isDimension: true
};

const postedField: CubeFieldSpec = {
    name: 'posted',
    type: 'date'
};

const amountField: CubeFieldSpec = {
    name: 'amount',
    type: 'number',
    aggregator: 'SUM'
};

const updatedField: CubeFieldSpec = {
    name: 'updated',
    type: 'date'
};

const updatedByField: CubeFieldSpec = {
    name: 'updatedBy',
    type: 'string'
};

const timestampField: CubeFieldSpec = {
    name: 'timestamp',
    type: 'auto'
};

export const allDistributionsFields: CubeFieldSpec[] = [
    ownerUserIdField,
    portfolioIdField,
    portfolioNameField,
    securityIdField,
    securityNameField,
    entityField,
    actionField,
    postedField,
    amountField,
    updatedField,
    updatedByField,
    timestampField
];

function isGroupRecord(record): boolean {
    return !endsWith(String(record.id), record.data.cubeLabel);
}

export const treeCol = (model: DistributionsModel): ColumnSpec => {
    return {
        field: {
            name: 'cubeLabel',
            displayName: 'Grouping Label'
        },
        headerName: () => {
            const {value} = model.groupingChooserModel,
                {store} = model.gridModel,
                names = value.map(field => store.getField(field).displayName);

            return names.join('->');
        },
        isTreeColumn: true,
        pinned: true,
        flex: 1,
        renderer: (v, {record}) => {
            if (isGroupRecord(record)) {
                return v;
            }
            return null;
        }
    };
};

export const idCol: ColumnSpec = {field: 'id', hidden: true, appData: {alwaysHide: true}};
export const portfolioIdCol: ColumnSpec = {
    field: 'portfolioId',
    hidden: true,
    appData: {alwaysHide: true}
};
export const securityIdCol: ColumnSpec = {
    field: 'securityId',
    hidden: true,
    appData: {alwaysHide: true}
};
export const entityCol: ColumnSpec = {field: 'entity', flex: 1};
export const portfolioNameCol: ColumnSpec = {
    field: 'portfolioName',
    headerName: 'Portfolio',
    flex: 1
};
export const securityNameCol: ColumnSpec = {field: 'securityName', displayName: 'Deal', flex: 2};

export const postedCol: ColumnSpec = {
    field: 'posted',
    minWidth: 120,
    renderer: v => fmtDate(v),
    editable: ({record}) => !isGroupRecord(record),
    editor: params =>
        dateEditor({
            ...params,
            inputProps: {enableTextInput: false, selectOnFocus: true, showPickerOnFocus: true}
        })
};

export const amountCol: ColumnSpec = {
    field: 'amount',
    align: 'right',
    renderer: v => fmtPrice(v, {precision: 2, prefix: '$'}),
    editable: ({record}) => !isGroupRecord(record),
    editor: params =>
        numberEditor({
            ...params,
            inputProps: {min: 1, precision: 2}
        }),
    minWidth: 120
};

export const distroActionCol: ColumnSpec = {
    field: 'action',
    renderer: v => actionBadge(v),
    editable: ({record}) => !isGroupRecord(record),
    editor: params =>
        selectEditor({
            ...params,
            inputProps: {
                options: ['Posted', 'Reinvested', 'Disbursed'],
                enableFilter: false,
                openMenuOnFocus: true,
                optionRenderer: opt => div({style: {padding: 5}, item: actionBadge(opt.value)}),
                valueRenderer: opt => actionBadge(opt.value)
            }
        }),
    minWidth: 120
};

export const updatedCol: ColumnSpec = {
    field: 'updated',
    renderer: v => fmtDateTimeSec(v),
    minWidth: 190
};
export const updatedByCol: ColumnSpec = {field: 'updatedBy', minWidth: 120};

export const buttonsCol = (model: DistributionsModel): ColumnSpec => {
    return {
        ...actionCol,
        width: calcActionColWidth(3),
        actions: [
            {
                icon: Icon.undo(),
                intent: 'primary',
                displayFn: ({record}) => ({
                    disabled: !record.isModified,
                    hidden: isGroupRecord(record)
                }),
                actionFn: ({record}) => record.store.revertRecords(record.id)
            },
            {
                icon: Icon.check(),
                intent: 'success',
                displayFn: ({record}) => ({
                    disabled: !record.isModified,
                    hidden: isGroupRecord(record)
                }),
                actionFn: ({record}) => model.update(record.data)
            },
            {
                icon: Icon.delete(),
                intent: 'danger',
                displayFn: ({record}) => ({
                    icon: record.isAdd ? Icon.close() : Icon.delete(),
                    hidden: isGroupRecord(record)
                }),
                actionFn: ({record}) => model.delete(record.raw)
            }
        ]
    };
};

export function actionBadge(action: string) {
    if (!action) return null;

    const baseClass = 'distribution-action-badge';
    return badge({
        item: action,
        className: classNames(baseClass, `${baseClass}__${action.toLowerCase()}`)
    });
}
